import React, { useEffect, useState } from 'react'
import qs from 'qs'
import { ISlackPostInstallationProps } from './types'
import { SeoTags } from '../../components/seo-tags'
import { App, Button, Col, Row, Skeleton, Typography } from 'antd'
import { SlackOutlined } from '@ant-design/icons'
import VtLogo from '../../assets/images/logo-purple.svg'
import IntlMessages from '../../util/IntlMessages'
import { getUserId as getSlackUserId } from '../../services/api/slack'
import { SlackAuth } from '../../services/auth/slack/slack'
import { track } from '../../services/analytics/analytics'
import { useHistory } from 'react-router'
import { updateSlackToken } from '../../services/api/slack'
import { FrontendUrls } from '../../types/urls'
import { getCompanyAndUser } from '../../services/api/companies'
import { useAppDispatch } from '../../store/hooks'
import { setUserId } from '../../store/user-id-slice'
import { SLACK_REQUIRED_BOT_SCOPES, SLACK_REQUIRED_USER_SCOPES } from '@vacationtracker/shared/data/slack'
import { useIntl } from 'react-intl'
import OtherPlatformLoginModal from '../../components/other-platform-login-modal'
import { IContactAdminData } from '../../types/auth'
import CompanyExistsModal from '../../components/modal-company-exists'
import { useManualQuery } from 'graphql-hooks'
import { IGetCompanyAndUserInfo } from '../../types/custom-queries'
import { getCompanyAndUserInfo } from '../../graphql/custom-queries'
import { confirmSigninWithCustomChallengeAnswer, IAuthNextStep, signin } from '@vacationtracker/shared/services/auth'
import { setAuthState } from '../../store/auth-state-slice'
import { AuthStateEnum } from '@vacationtracker/shared/types/auth-state'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { saveRememberedLogin } from '../../util/remembered-login'
import { SIGNIN_CANCELLED } from '../../util/openid/browser-runtime-v2'
import omit from 'lodash/omit'
import axios from 'axios'
const { Paragraph, Title } = Typography
const slackAuth = new SlackAuth(process.env.REACT_APP_SLACK_CLIENT_ID)

const SlackInstallBot = (props: ISlackPostInstallationProps): React.ReactElement => {
  const { notification } = App.useApp()
  const { match } = props

  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const history = useHistory()
  const [userName, setUserName] = useState<string | null>(null)
  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [slackTeamId, setSlackTeamId] = useState<string | null>(match.params.teamId)
  const [slackTeamName, setSlackTeamName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoadingStatus] = useState(false)
  const [otherPlatformLogin, setOtherPlatformLogin] = useState(false)
  const [existsOnAnotherPlatform, setExistsOnAnotherPlatform] = useState('')
  const [existingOrganizationName, setExistingOrganizationName] = useState('')
  const [contactAdminModal, setContactAdminModal] = useState<IContactAdminData | null>(null)
  const [companyExists, setCompanyExists] = useState(false)

  const [getCompanyAndUserInfoQuery] = useManualQuery<IGetCompanyAndUserInfo, { userId: string }>(getCompanyAndUserInfo)

  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true })
  useEffect(() => {
    if (queryParams.realName && queryParams.realName !== '') {
      setUserName(queryParams.realName as string)
    }
  }, [queryParams])

  // Opens the install step of the sign-up funnel. Nobody can report "did not install" directly,
  // so this is the denominator: everyone who reached the step, against the click below.
  useEffect(() => {
    track('SIGNUP_SLACK_INSTALL_PAGE_VIEWED', { platform: PlatformEnum.Slack })
  }, [])

  const installSlackApp = async () => {
    setError(null)
    setLoadingStatus(true)
    track('SIGNUP_SLACK_INSTALL_BUTTON_CLICKED', { platform: PlatformEnum.Slack })
    let userData
    try {
      const slackInstallResponse = await slackAuth.signin(SLACK_REQUIRED_BOT_SCOPES, SLACK_REQUIRED_USER_SCOPES, slackTeamId)

      const slackUserId = slackInstallResponse.authed_user.id
      const userToken = slackInstallResponse.authed_user.access_token
      const botToken = slackInstallResponse.access_token
      const teamId = slackInstallResponse.team.id
      const teamName = slackInstallResponse.team.name

      slackAuth.setBotToken(botToken)
      slackAuth.setUserToken(userToken)

      setSlackTeamName(teamName)

      userData = await getSlackUserId(slackUserId, userToken)
      if (userData.vacationTrackerId) {
        if (userData.mail) {
          const isActive = await isUserActive(userData.mail)
          if (!isActive) {
            history.push(FrontendUrls.signup)
            return
          }
        }
      }
      const user = await slackAuth.getUser(slackUserId, botToken)

      const emailOfAuthedUser: string = userData.mail || slackInstallResponse.authed_user?.email || ''

      setUserEmail(emailOfAuthedUser)

      setUserName((user.real_name as string) || (user.name as string))

      if (userData.type === 'COMPANY_NOT_FOUND') {
        localStorage.setItem(
          'vtCreateUser',
          JSON.stringify({
            name: userData.name,
            id: userData.id,
            mail: emailOfAuthedUser,
            userEmail: emailOfAuthedUser,
            platform: 'slack',
            slackBotToken: botToken,
            slackUserToken: userToken,
            slackUserId: slackUserId,
            teamName: teamName,
            slackTeamId: teamId,
            slackOrgTeamId: slackInstallResponse.enterprise?.id,
            slackBotId: slackInstallResponse.bot_user_id,
            imageUrl: userData?.slackUser?.imageUrl,
            isOwner: userData?.slackUser?.isOwner,
            isAdmin: userData?.slackUser?.isAdmin,
            timezone: userData?.slackUser?.timezone,
          })
        )

        if (userData.existsOnAnotherPlatform && userData.existingOrganizationName) {
          track('ORG_EXISTS_ON_ANOTHER_PLATFORM_OPEN_MODAL', { platform: 'slack', existingOrganizationName: userData.existingOrganizationName })
          setExistsOnAnotherPlatform(userData.existsOnAnotherPlatform)
          setExistingOrganizationName(userData.existingOrganizationName)
          setOtherPlatformLogin(true)
          return
        }

        setLoadingStatus(false)
        dispatch(setAuthState(AuthStateEnum.signUp))
        // The bot is in. The sign-up itself still has to finish on company details, which is
        // where SIGNUP_WITH_SLACK_SUCCESS fires.
        track('SIGNUP_SLACK_INSTALL_COMPLETED', { platform: PlatformEnum.Slack })

        history.push(`${FrontendUrls.signup}${FrontendUrls.companyDetails}`)
        return
      }

      if (userData.type === 'USER_NOT_FOUND' && userData.subscriptionStatus === 'canceled') {
        notification.error({
          message: formatMessage({ id: 'connect.subscriptionExpiredTitle' }),
          description: formatMessage({ id: 'error.subscriptionExpired' }),
          duration: 0,
        })
        return
      }

      if (userData.type === 'USER_NOT_FOUND') {
        setContactAdminModal({
          platform: 'slack',
          organizationName: userData.organizationName || '',
          contactEmail: userData.contactEmail || '',
          adminContacts: userData.adminContacts || [],
        })
        track('ORG_ALREADY_EXISTS_PAGE_VIEWED', {
          ...omit(userData || {}, ['adminContacts', 'slackUser']),
          platform: 'slack',
        })
        setCompanyExists(true)
        setLoadingStatus(false)
        return
      }

      const signInResponse = (await signin(userData.vacationTrackerId)) as IAuthNextStep
      if (signInResponse.challengeName === 'CUSTOM_CHALLENGE' && signInResponse.challengeParam?.question === 'token') {
        const cognitoResponse = await confirmSigninWithCustomChallengeAnswer(userToken, { loginType: 'slack' })
        const cognitoUsername = cognitoResponse.username as string
        localStorage.setItem('userId', cognitoUsername)

        try {
          await updateSlackToken(userToken, botToken)
        } catch (error) {
          if (error?.response?.data?.error && (error?.response.data.error === 'error.botTokenNotExist' || error?.response.data.error === 'error.botTokenNotValid')) {
            setLoadingStatus(false)
            setError('bot_token_not_valid')
            return
          }
        }
        dispatch(setUserId(cognitoUsername))
        // Signing in happens here too, not only on the sign-in page, so the marker has to be
        // written here as well or the next visit offers whichever account was recorded last.
        saveRememberedLogin({ email: emailOfAuthedUser, platform: PlatformEnum.Slack })
        // Same ending as on the sign-up page: they came to sign up and already had an account.
        track('SIGNUP_EXISTING_ACCOUNT_SIGNED_IN', { platform: PlatformEnum.Slack })
        await getCompanyAndUser(cognitoUsername, getCompanyAndUserInfoQuery, dispatch, history)
        dispatch(setAuthState(AuthStateEnum.signedIn))
        history.push(FrontendUrls.dashboard)
      }

      setLoadingStatus(false)
    } catch (error) {
      console.log('error', error)
      const errorMessage = typeof error === 'string' ? error : (error.message as string)
      // Separated from an abandoned install: this one tried and the install failed, which is a
      // different problem from never pressing the button.
      const cancelled = typeof errorMessage === 'string' && (errorMessage.includes(SIGNIN_CANCELLED) || errorMessage.includes('access_denied'))
      track(cancelled ? 'SIGNUP_WITH_SLACK_CANCELED' : 'SIGNUP_WITH_SLACK_ERROR', {
        platform: PlatformEnum.Slack,
        errorMessage,
        step: 'install',
      })
      setError(errorMessage)
      setLoadingStatus(false)
    }
  }

  const isUserActive = async (email: string): Promise<boolean> => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/core/auth-info`, { email })
      return true
    } catch (err) {
      if (err?.response?.data?.code === 'EmailPlatformUserInactive') {
        notification.error({
          message: formatMessage({ id: 'error.signin.inactiveUser' }),
          key: 'USER_INACTIVE',
          description: formatMessage({ id: 'error.auth.companyExists.line1' }),
          duration: 0,
        })
        return false
      }
      return true
    }
  }

  const connectWithPlatform = (platform: string) => {
    history.push(FrontendUrls.signin + `?platform=${platform}`)
  }

  const closeOtherPlatformLoginModal = () => {
    track('ORG_EXISTS_ON_ANOTHER_PLATFORM_CLOSE_MODAL', {})
    history.push(FrontendUrls.signup)
  }

  useEffect(() => {
    if (match.params.teamId) {
      setSlackTeamId(match.params.teamId)
    }
  }, [match.params.teamId])

  return (
    <div className="auth-wrap">
      <SeoTags title="connect.meta.title.signup" description="connect.meta.description" />
      <div className="auth-container" style={{ paddingLeft: '20px', paddingRight: '20px' }}>
        <Row
          align="middle"
          justify="space-around"
          gutter={[
            { xs: 16, lg: 48 },
            { xs: 16, lg: 48 },
          ]}
        >
          <Col span={24}>
            <div className="auth-sidebar-logo">
              <VtLogo />
            </div>
          </Col>
          <Col xs={{ span: 20 }} lg={{ span: error === null ? 14 : 24 }}>
            {error === null ? (
              <Skeleton loading={loading} paragraph={{ rows: 2 }} active>
                <Title level={3} style={{ textAlign: 'center' }}>
                  {
                    <IntlMessages
                      id="connect.welcome"
                      values={{
                        name: userName,
                      }}
                    />
                  }
                </Title>
                <Paragraph style={{ textAlign: 'center' }}>
                  <IntlMessages id="connect.slackPostInstallation1" />
                </Paragraph>
                <Paragraph style={{ textAlign: 'center' }}>
                  <IntlMessages id="connect.slackPostInstallation2" />
                </Paragraph>
                <Paragraph style={{ textAlign: 'center' }}>
                  <IntlMessages id="connect.slackPostInstallation3" />
                </Paragraph>
              </Skeleton>
            ) : (
              <>
                <Title level={3} style={{ textAlign: 'center' }}>
                  Install Vacation Tracker bot
                </Title>
                {error === 'invalid_code' ? (
                  <Paragraph style={{ textAlign: 'center' }}>
                    <IntlMessages id="connect.slackPostInstallationError.invalidCode" />
                  </Paragraph>
                ) : (
                  <Paragraph style={{ textAlign: 'center' }}>
                    <IntlMessages id="connect.slackPostInstallationError.generic" />
                  </Paragraph>
                )}
                <Paragraph style={{ textAlign: 'center' }}>Click the button below to install the Vacation Tracker bot to your Slack workspace.</Paragraph>
              </>
            )}
          </Col>
          <Col xs={{ span: 20 }} lg={{ span: 12 }}>
            {loading ? (
              <Skeleton.Button style={{ width: '200px' }} active />
            ) : (
              <Button icon={<SlackOutlined />} size="large" className="vt-auth" type="primary" block onClick={() => installSlackApp()}>
                <IntlMessages id="connect.signInWithSlackInstallBot" />
              </Button>
            )}
          </Col>
        </Row>
      </div>
      {companyExists && contactAdminModal && userEmail && (
        <CompanyExistsModal
          companyExists={companyExists}
          setCompanyExists={setCompanyExists}
          contactAdminModal={contactAdminModal}
          userEmail={userEmail}
          userWorkspaceName={slackTeamName ?? undefined}
        />
      )}
      {otherPlatformLogin && (
        <OtherPlatformLoginModal
          otherPlatformLogin={otherPlatformLogin}
          connectWithPlatform={connectWithPlatform}
          closeOtherPlatformLoginModal={closeOtherPlatformLoginModal}
          existingOrganizationName={existingOrganizationName}
          existsOnAnotherPlatform={existsOnAnotherPlatform}
          selectedPlatform={PlatformEnum.Slack}
        />
      )}
    </div>
  )
}

export default SlackInstallBot
